/**
 * Template Library Component
 * Main interface for browsing, searching, and managing workflow templates
 */

import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  StarIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface Template {
  id: string;
  name: string;
  version: string;
  description: string;
  author: string;
  category: string;
  tags: string[];
  stats?: {
    rating: number;
    ratingCount: number;
    downloads: number;
    forks: number;
  };
  createdAt: string;
  updatedAt: string;
}

const TemplateLibrary: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'my-templates' | 'marketplace' | 'featured' | 'popular'>('my-templates');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Mock data for now - will be replaced with API calls
  const mockTemplates: Template[] = [
    {
      id: 'retry-pattern',
      name: 'Retry Pattern',
      version: '1.0.0',
      description: 'Configurable retry pattern with exponential backoff for handling transient failures',
      author: 'System',
      category: 'resilience',
      tags: ['retry', 'resilience', 'error-handling'],
      stats: {
        rating: 4.8,
        ratingCount: 24,
        downloads: 156,
        forks: 12
      },
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-15T10:00:00Z'
    },
    {
      id: 'saga-pattern',
      name: 'Saga Pattern',
      version: '1.0.0',
      description: 'Distributed transaction pattern with compensation for microservices',
      author: 'System',
      category: 'transaction',
      tags: ['saga', 'transaction', 'compensation', 'distributed'],
      stats: {
        rating: 4.6,
        ratingCount: 18,
        downloads: 89,
        forks: 8
      },
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-15T10:00:00Z'
    },
    {
      id: 'fan-out-fan-in',
      name: 'Fan-Out/Fan-In Pattern',
      version: '1.0.0',
      description: 'Parallel execution with result aggregation for high-throughput processing',
      author: 'System',
      category: 'parallel',
      tags: ['fan-out', 'fan-in', 'parallel', 'aggregation'],
      stats: {
        rating: 4.7,
        ratingCount: 31,
        downloads: 203,
        forks: 15
      },
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-15T10:00:00Z'
    }
  ];

  const categories = [
    'All Categories',
    'resilience',
    'transaction', 
    'parallel',
    'data-processing',
    'integration',
    'automation'
  ];

  useEffect(() => {
    loadTemplates();
  }, [activeTab, searchQuery, selectedCategory]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      let filteredTemplates = mockTemplates;
      
      if (searchQuery) {
        filteredTemplates = filteredTemplates.filter(template =>
          template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      }
      
      if (selectedCategory && selectedCategory !== 'All Categories') {
        filteredTemplates = filteredTemplates.filter(template =>
          template.category === selectedCategory
        );
      }
      
      setTemplates(filteredTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    alert('Template creation will be implemented with the visual editor');
  };

  const handleViewTemplate = (template: Template) => {
    alert(`Viewing template: ${template.name}`);
  };

  const handleDownloadTemplate = (template: Template) => {
    alert(`Downloading template: ${template.name}`);
  };

  const handleForkTemplate = (template: Template) => {
    alert(`Forking template: ${template.name}`);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <StarIconSolid key={i} className="h-4 w-4 text-yellow-400" />
        );
      } else {
        stars.push(
          <StarIcon key={i} className="h-4 w-4 text-gray-300" />
        );
      }
    }

    return stars;
  };

  const renderTemplateCard = (template: Template) => (
    <div key={template.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{template.name}</h3>
          <p className="text-sm text-gray-600 mb-2">v{template.version} • {template.author}</p>
          <p className="text-gray-700 text-sm mb-3">{template.description}</p>
        </div>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {template.category}
        </span>
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {template.tags.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
          >
            {tag}
          </span>
        ))}
        {template.tags.length > 3 && (
          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
            +{template.tags.length - 3}
          </span>
        )}
      </div>

      {template.stats && (
        <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
          <div className="flex items-center space-x-1">
            {renderStars(template.stats.rating)}
            <span className="ml-1">({template.stats.ratingCount})</span>
          </div>
          <div className="flex space-x-4">
            <span>{template.stats.downloads} downloads</span>
            <span>{template.stats.forks} forks</span>
          </div>
        </div>
      )}

      <div className="flex space-x-2">
        <button
          onClick={() => handleViewTemplate(template)}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <EyeIcon className="h-4 w-4 mr-1" />
          View
        </button>
        <button
          onClick={() => handleDownloadTemplate(template)}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
          Use
        </button>
        <button
          onClick={() => handleForkTemplate(template)}
          className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <DocumentDuplicateIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Template Library</h1>
          <p className="mt-2 text-gray-600">
            Discover and manage reusable workflow templates and patterns
          </p>
        </div>
        <button
          onClick={handleCreateTemplate}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Template
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'my-templates', label: 'My Templates' },
            { key: 'marketplace', label: 'Marketplace' },
            { key: 'featured', label: 'Featured' },
            { key: 'popular', label: 'Popular' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div className="sm:w-48">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {categories.map((category) => (
              <option key={category} value={category === 'All Categories' ? '' : category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading templates...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(renderTemplateCard)}
          {templates.length === 0 && (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
              <p className="text-gray-600">
                {activeTab === 'my-templates' 
                  ? "Create your first template to get started"
                  : "Try adjusting your search criteria"
                }
              </p>
            </div>
          )}
        </div>
      )}

      {/* Info Banner */}
      <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <div className="text-blue-400 text-2xl">🚀</div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Template System Features
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Parameterized templates with validation</li>
                <li>Common workflow patterns (Retry, Saga, Fan-out/Fan-in)</li>
                <li>Template versioning and inheritance</li>
                <li>Community marketplace with ratings</li>
                <li>Template forking and customization</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibrary;
